import { Grid, Tooltip } from '@mui/material'
import { AddCircle, LocalAtmRounded, RemoveCircle } from '@mui/icons-material'
import { connect, FormikContextType, getIn } from 'formik'
import React, { useEffect } from 'react'
import FullWidthField from '../common/FullWidthField'
import { IClaimFormValues } from './ClaimFormValues'
import { createLineItemFormValues, ILineItemFormValues } from './LineItemFormValues'
import DatePickerField from 'src/common/DatePickerField/DatePickerField'

export interface IHaveLineItemFormValues {
  lineItems: ILineItemFormValues
}

interface IBox24Props {
  index: number
  openContractModal: () => void
  disabled?: boolean
  disableDollarAmountEditing?: boolean
  disableLineItemActions?: boolean
  providerTypeId?: string
  providerId?: string
  showContractAmount: boolean
}

interface IFormikProps {
  formik: FormikContextType<IClaimFormValues>
}

const Box24: React.FC<IBox24Props & IFormikProps> = (props) => {
  const {
    index,
    formik,
    openContractModal,
    disabled = false,
    disableDollarAmountEditing = false,
    disableLineItemActions = false,
  } = props
  const createName = (field: string) => `q24.${index}.${field}`
  const hasValue = (field: string) => {
    const name = createName(field)
    const value = getIn(formik.values, name)
    return !!value
  }
  const showLabels = index === 0
  const dateOfServiceFrom = getIn(formik.values, createName('aDateOfServiceFrom'))
  const dateOfServiceTo = getIn(formik.values, createName('aDateOfServiceTo'))

  const updateOtherDate =
    (field: 'aDateOfServiceFrom' | 'aDateOfServiceTo') => (date: Date | undefined) => {
      const otherField =
        field === 'aDateOfServiceFrom' ? 'aDateOfServiceTo' : 'aDateOfServiceFrom'
      formik.setFieldValue(createName(otherField), date)
    }

  useEffect(() => {
    if (formik.values.dateOfService && !dateOfServiceFrom && !dateOfServiceTo) {
      const dateValue = formik.values.dateOfService
      formik.setFieldValue(createName('aDateOfServiceFrom'), dateValue)
      formik.setFieldValue(createName('aDateOfServiceTo'), dateValue)
    }
  }, [dateOfServiceFrom, dateOfServiceTo, formik.values.dateOfService])

  function renderIcons(totalLines: number, lineNumber: number) {
    const canModifyLineItems = !disabled && !disableLineItemActions
    const removeCircleStyle = {
      color: canModifyLineItems ? '#930A0A' : '#9e9e9e',
      cursor: canModifyLineItems ? 'pointer' : 'not-allowed',
      fontSize: '30px',
    } as React.CSSProperties

    const addCircleStyle = {
      color: canModifyLineItems ? '#29348F' : '#9e9e9e',
      cursor: canModifyLineItems ? 'pointer' : 'not-allowed',
      fontSize: '30px',
    } as React.CSSProperties

    const showRemoveIcon = totalLines > 1
    const showAddIcon = lineNumber === totalLines - 1

    return (
      <Grid
        item
        xs={2}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
      >
        {showRemoveIcon && (
          <RemoveCircle
            style={removeCircleStyle}
            aria-disabled={!canModifyLineItems}
            onClick={canModifyLineItems ? removeLineItem(lineNumber) : undefined}
          />
        )}
        {showAddIcon && (
          <AddCircle
            style={addCircleStyle}
            aria-disabled={!canModifyLineItems}
            onClick={canModifyLineItems ? renderFieldClick : undefined}
            onKeyDown={canModifyLineItems ? renderFieldKeyDown : undefined}
            tabIndex={canModifyLineItems ? 0 : -1}
          />
        )}
      </Grid>
    )
  }

  const removeLineItem = (lineIndex: number) => () => {
    const codeList = formik.values.q24
    codeList.splice(lineIndex, 1)
    formik.setFieldValue('q24', codeList)
  }

  const onFocus = () => {
    if (formik.values.dateOfService) {
      if (!hasValue('aDateOfServiceFrom')) {
        formik.setFieldValue(
          createName('aDateOfServiceFrom'),
          formik.values.dateOfService
        )
      }
      setDatesEqual()
    }
  }

  const renderFieldClick = () => {
    const newLineItem = createLineItemFormValues()
    newLineItem.aDateOfServiceFrom = formik.values.dateOfService
    newLineItem.aDateOfServiceTo = formik.values.dateOfService
    const codeList = formik.values.q24

    if (codeList.length > 0) {
      newLineItem.iNpi = codeList[codeList.length - 1].iNpi
      newLineItem.bPlaceOfServiceTo = codeList[codeList.length - 1].bPlaceOfServiceTo
    }

    formik.setFieldValue('q24', codeList.concat([newLineItem]))
    onFocus()
  }

  const renderFieldKeyDown = (event: any) => {
    const code = event.keyCode || event.which
    if (code === 13 || code === 32) {
      renderFieldClick()
    }
  }

  const setDatesEqual = () => {
    const q24 = formik.values.q24
    q24[index].aDateOfServiceTo = q24[index].aDateOfServiceFrom
  }

  function renderField() {
    var disable = !(
      props.providerTypeId &&
      props.providerId &&
      hasValue('dProcedureCptHcpcs')
    )
    var showContractModal = props.showContractAmount
    return (
      <Grid
        container
        direction="row"
        spacing={2}
        alignItems="center"
        wrap="nowrap"
        style={{ flexWrap: 'nowrap', overflowX: 'auto' }}
      >
        <Grid item xs={3}>
          <DatePickerField
            inputId="aDateOfServiceFrom"
            variant="standard"
            name={createName('aDateOfServiceFrom')}
            label="DOS From"
            disabled={disabled}
            required
            onDateChange={updateOtherDate('aDateOfServiceFrom')}
          />
        </Grid>
        <Grid item xs={3}>
          <DatePickerField
            inputId="aDateOfServiceTo"
            variant="standard"
            name={createName('aDateOfServiceTo')}
            label="DOS To"
            disabled={disabled}
            required
            onDateChange={updateOtherDate('aDateOfServiceTo')}
          />
        </Grid>
        <Grid item md={2} xs={1}>
          <FullWidthField
            variant="standard"
            name={createName('bPlaceOfServiceTo')}
            label={showLabels ? 'Place of Service' : ' '}
            InputLabelProps={{ shrink: true }}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={2}>
          <FullWidthField
            variant="standard"
            name={createName('dProcedureCptHcpcs')}
            label="CPT/HCPCS"
            InputLabelProps={{ shrink: true }}
            disabled={disabled}
            required
          />
        </Grid>
        <Grid item xs={3}>
          <Grid container spacing={1}>
            <Grid item xs={3}>
              <FullWidthField
                variant="standard"
                name={createName('dModifier1')}
                label={showLabels ? 'MODIFIER' : ' '}
                InputLabelProps={{ shrink: true }}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={3}>
              <FullWidthField
                variant="standard"
                name={createName('dModifier2')}
                label=" "
                InputLabelProps={{ shrink: true }}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={3}>
              <FullWidthField
                variant="standard"
                name={createName('dModifier3')}
                label=" "
                InputLabelProps={{ shrink: true }}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={3}>
              <FullWidthField
                variant="standard"
                name={createName('dModifier4')}
                label=" "
                InputLabelProps={{ shrink: true }}
                disabled={disabled}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={2}>
          <FullWidthField
            variant="standard"
            name={createName('eDiagnosisPointer')}
            label={showLabels ? 'Diagnosis Pointer' : ' '}
            InputLabelProps={{ shrink: true }}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={2}>
          <FullWidthField
            variant="standard"
            name={createName('fCharges')}
            label="Charges"
            InputLabelProps={{ shrink: true }}
            disabled={disabled || disableDollarAmountEditing}
            required
          />
        </Grid>
        <Grid item xs={2} style={{ paddingRight: '0px' }}>
          <FullWidthField
            variant="standard"
            name={createName('contractAmount')}
            label={showLabels ? 'Contract Amount' : ' '}
            InputLabelProps={{ shrink: true }}
            disabled={disabled || disableDollarAmountEditing}
          />
        </Grid>
        {showContractModal && !disableDollarAmountEditing && (
          <Grid
            item
            xs={1}
            style={{
              paddingRight: '10px',
              paddingTop: '20px',
              alignItems: 'flex-start',
              display: 'flex',
            }}
          >
            <Tooltip title="Choose Historical Contract Amount">
              <LocalAtmRounded
                fontSize="large"
                color={disable ? 'disabled' : 'primary'}
                style={{ cursor: disable ? 'auto' : 'pointer' }}
                onClick={
                  disable
                    ? () => {}
                    : () => {
                        openContractModal()
                      }
                }
              />
            </Tooltip>
          </Grid>
        )}
        <Grid item xs={2}>
          <FullWidthField
            variant="standard"
            name={createName('gDaysOrUnits')}
            label="Quantity"
            InputLabelProps={{ shrink: true }}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={3}>
          <FullWidthField
            variant="standard"
            name={createName('iNpi')}
            label={showLabels ? 'NPI' : ' '}
            InputLabelProps={{ shrink: true }}
            disabled={disabled}
          />
        </Grid>
        <Grid
          item
          xs={1}
          style={{
            minWidth: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          {renderIcons(formik.values.q24.length, index)}
        </Grid>
      </Grid>
    )
  }

  return <Grid item>{renderField()}</Grid>
}

export default connect<IBox24Props, IClaimFormValues>(Box24)
